import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Phone, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { API, CONTACT, EVENT_TYPES, formatApiError } from "@/lib/site";

const inputCls =
  "rounded-none border-stone-300 bg-white py-6 text-stone-900 placeholder:text-stone-400 focus-visible:ring-brand-bronze";

const emptyQuote = { name: "", email: "", phone: "", event_type: "", event_date: "", location: "", message: "" };
const emptyContact = { name: "", email: "", phone: "", message: "" };

export function QuoteSection() {
  const [form, setForm] = useState(emptyQuote);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email so we can reach you.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Quote request received — we'll be in touch shortly.");
      setForm(emptyQuote);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="border-y border-brand-bronze/20 bg-gradient-to-b from-brand-bronze-light/40 to-stone-50 py-24 lg:py-32">
      <div className="mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-12 lg:px-10">
        <div className="lg:col-span-5">
          <SectionHeading
            overline="Request a Quote"
            title={
              <>
                Tell us about <span className="italic bronze-text">your event</span>
              </>
            }
            description="Share a few details and we'll respond with availability and exact pricing for your dates, trailer, and venue — no obligation, no pressure."
          />
          <Reveal delay={0.15}>
            <ul className="mt-10 space-y-4 text-sm font-light text-stone-600">
              <li className="flex items-center gap-3">
                <span className="inline-block h-1.5 w-1.5 rotate-45 bg-brand-bronze" aria-hidden="true" />
                Exact pricing for your dates, trailer &amp; venue
              </li>
              <li className="flex items-center gap-3">
                <span className="inline-block h-1.5 w-1.5 rotate-45 bg-brand-bronze" aria-hidden="true" />
                Delivery-fee confirmation for your location
              </li>
              <li className="flex items-center gap-3">
                <span className="inline-block h-1.5 w-1.5 rotate-45 bg-brand-bronze" aria-hidden="true" />
                Guidance on the right trailer for your guest count
              </li>
            </ul>
          </Reveal>
        </div>
        <Reveal delay={0.1} className="lg:col-span-7">
          <form onSubmit={submit} data-testid="quote-form" className="border border-stone-200 bg-white p-8 shadow-[0_24px_60px_-30px_rgb(0,0,0,0.15)] sm:p-10">
            <div className="grid gap-5 sm:grid-cols-2">
              <Input required placeholder="Full name *" value={form.name} onChange={set("name")} className={inputCls} data-testid="quote-name-input" aria-label="Full name" />
              <Input required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} data-testid="quote-email-input" aria-label="Email" />
              <Input type="tel" placeholder="Phone" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="quote-phone-input" aria-label="Phone" />
              <Select value={form.event_type} onValueChange={set("event_type")}>
                <SelectTrigger className={inputCls} data-testid="quote-event-type-select" aria-label="Event type">
                  <SelectValue placeholder="Event type" />
                </SelectTrigger>
                <SelectContent>
                  {EVENT_TYPES.map((t) => (
                    <SelectItem key={t} value={t} data-testid={`quote-event-type-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input type="date" value={form.event_date} onChange={set("event_date")} className={inputCls} data-testid="quote-date-input" aria-label="Event date" />
              <Input placeholder="Event location / venue" value={form.location} onChange={set("location")} className={inputCls} data-testid="quote-location-input" aria-label="Event location" />
            </div>
            <Textarea
              placeholder="Tell us about your event — guest count, venue style, anything that helps."
              value={form.message}
              onChange={set("message")}
              className={`${inputCls} mt-5 min-h-28`}
              data-testid="quote-message-input"
              aria-label="Message"
            />
            <button
              type="submit"
              disabled={sending}
              data-testid="quote-submit-button"
              className="mt-8 inline-flex w-full items-center justify-center gap-3 bg-stone-900 px-8 py-4 text-sm tracking-wide text-stone-50 transition-colors duration-300 hover:bg-brand-bronze hover:text-stone-900 disabled:opacity-60"
            >
              {sending ? "Sending…" : "Request My Quote"}
              <Send className="h-4 w-4" aria-hidden="true" />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}

export function ContactSection() {
  const [form, setForm] = useState(emptyContact);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete your name, email, and message.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent — thank you for reaching out.");
      setForm(emptyContact);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const details = [
    { icon: Phone, label: "Call us", value: CONTACT.phoneDisplay, href: CONTACT.phoneHref, testid: "contact-phone-link" },
    { icon: Mail, label: "Email", value: CONTACT.email, href: CONTACT.emailHref, testid: "contact-email-link" },
    { icon: MapPin, label: "Find us", value: `${CONTACT.address} · ${CONTACT.region}`, href: null, testid: "contact-address" },
  ];

  return (
    <section id="contact" data-testid="contact-section" className="bg-white py-24 lg:py-32">
      <div className="mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-12 lg:px-10">
        <div className="lg:col-span-5">
          <SectionHeading
            overline="Contact"
            title={
              <>
                Let's talk about <span className="italic bronze-text">your dates</span>
              </>
            }
            description="Questions about trailers, availability, or your venue? Call, email, or send a note — a real person answers."
          />
          <div className="mt-10 space-y-6">
            {details.map((d, i) => (
              <Reveal key={d.label} delay={0.1 + i * 0.08}>
                <div className="flex items-center gap-5 border-b border-stone-200 pb-6">
                  <span className="flex h-12 w-12 items-center justify-center border border-brand-bronze/30 text-brand-bronze-dark">
                    <d.icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="text-xs font-semibold tracking-[0.2em] uppercase text-stone-400">{d.label}</p>
                    {d.href ? (
                      <a href={d.href} data-testid={d.testid} className="link-underline text-lg font-light text-stone-900">
                        {d.value}
                      </a>
                    ) : (
                      <p className="text-lg font-light text-stone-900" data-testid={d.testid}>{d.value}</p>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <Reveal delay={0.15} className="lg:col-span-7">
          <form onSubmit={submit} data-testid="contact-form" className="border border-stone-200 bg-stone-50 p-8 sm:p-10">
            <div className="grid gap-5 sm:grid-cols-2">
              <Input required placeholder="Full name *" value={form.name} onChange={set("name")} className={inputCls} data-testid="contact-name-input" aria-label="Full name" />
              <Input required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} data-testid="contact-email-input" aria-label="Email" />
            </div>
            <Input type="tel" placeholder="Phone" value={form.phone} onChange={set("phone")} className={`${inputCls} mt-5`} data-testid="contact-phone-input" aria-label="Phone" />
            <Textarea
              required
              placeholder="How can we help? *"
              value={form.message}
              onChange={set("message")}
              className={`${inputCls} mt-5 min-h-36`}
              data-testid="contact-message-input"
              aria-label="Message"
            />
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="mt-8 inline-flex w-full items-center justify-center gap-3 border border-stone-900 px-8 py-4 text-sm tracking-wide text-stone-900 transition-colors duration-300 hover:bg-stone-900 hover:text-stone-50 disabled:opacity-60"
            >
              {sending ? "Sending…" : "Send Message"}
              <Send className="h-4 w-4" aria-hidden="true" />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
